import { useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../services/api';
import { onAngularEvent, trackEvent } from '../../services/angular-bridge';
import { notifySuccess } from '../../services/notify';

export interface AuditLogEntry {
  id: string;
  date: number;
  user: string;
  accountId: string;
  projectId: string;
  ipAddress: string;
  actionDescription: string;
  actionDetails: Record<string, unknown>;
  authenticated: boolean;
}

export interface AuditLogPage {
  records: AuditLogEntry[];
  totalRecords: number;
  totalPageCount: number;
}

export interface AuditLogParams {
  page: number;
  limit: number;
}

export function useAuditLog(projectId: string, params: AuditLogParams) {
  return useQuery<AuditLogPage>({
    queryKey: ['auditLog', projectId, params],
    queryFn: () => api.post<AuditLogPage>(
      `/auditing/project/${projectId}?limit=${params.limit}&page=${params.page}`,
      [{ sortBy: 'date', sortDirection: 'DESC' }],
    ),
    enabled: !!projectId,
  });
}

export function useGenerateAuditReport(projectId: string) {
  useEffect(() => {
    return onAngularEvent('FeedService.ReportComplete', (message: { name: string; directDownloadPath: string }) => {
      notifySuccess({
        title: 'Report available',
        description: `Report: ${message.name} has completed.`,
        buttons: `<a class="btn btn-success" href="${message.directDownloadPath}"><i class="icon-download-alt"></i> Download</a>`,
      });
    });
  }, [projectId]);

  return useMutation({
    mutationFn: () => {
      trackEvent('download audit log report', 'clicked', projectId);
      notifySuccess({
        title: 'Report started',
        description: 'Name: Audit Log Report',
        timeout: 5000,
      });
      return api.post(`/project/${projectId}/report`, {
        projectId,
        type: 'audit_log_report',
        name: 'Audit Log Report',
        format: 'csv',
        multiValueCharCode: '0',
        dateTimeFormat: 'yyyy-MM-dd',
        hasHeaderRow: false,
        fields: [],
      });
    },
  });
}
